import EmptyState from '../util/EmptyState';
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faPenToSquare, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import Paginations from '../util/Paginations';
import { t } from 'i18next';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import GoToReview from '../modal/GoToReview';
import { Tooltip } from '@mui/material';
import DeleteDocs from '../modal/DeleteDocs';
import EditDocs from '../modal/EditDocs';
import Modal from '@mui/material/Modal';
import DataTable, { ColDef } from '../util/DataTable';

interface ReportsTableProps {
  docs: any,
  baseUrl: any,
  refreshCategoryDocs: any,
  access: any,
  viewMode?: 'list' | 'cards',
  catFilter?: number[],
  category: any,
  selectedCategoryId: any,
  handleClickCategoryBox: any,
  categoryName: any,
  isCategoryListVisible: any,
  handleCategoryListClick: any,
  handleNodeSelect: any,
  handleClickAddCategoryId: any,
  setSelectedCategoryId: any,
  setCategoryName: any,
  none: any,
  setNone: any,
  panelDoc?: any,
  setPanelDoc?: (doc: any) => void,
  onOpenPolicy?: (doc: any) => void,
}

const DocsTable: React.FC<ReportsTableProps> = ({
  docs,
  baseUrl,
  refreshCategoryDocs,
  access,
  viewMode = 'list',
  catFilter = [],
  category,
  selectedCategoryId,
  handleClickCategoryBox,
  categoryName,
  isCategoryListVisible,
  handleCategoryListClick,
  handleNodeSelect,
  handleClickAddCategoryId,
  setSelectedCategoryId,
  setCategoryName,
  none,
  setNone,
  panelDoc,
  setPanelDoc,
  onOpenPolicy }) => {
  const [page, setPage] = useState(0);
  const [documentId, setDocumentId] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const { enqueueSnackbar } = useSnackbar();

  const handleOpenPolicy = (doc: any) => {
    if (onOpenPolicy) onOpenPolicy(doc);
  };

  const sendToReview = async () => {
    try {
      await axios.put(`document-learn/status/${documentId}`, { status: 'learned' });
      refreshCategoryDocs();
      setShowReview(false);
      enqueueSnackbar('Success', { variant: 'success' });
    } catch {
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const handleShow = (id: any, set: any) => {
    set(true);
    setDocumentId(id);
    if (set === setShowEdit) setNone(true);
  };

  const handleCloseEdit = () => {
    setShowEdit(false);
    setNone(false);
  };

  const fileKind = (url: string) => {
    const ext = (url || '').split('.').pop()?.toLowerCase();
    if (ext === 'doc' || ext === 'docx') return { label: 'DOC', cls: 'doc' };
    if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return { label: 'XLS', cls: 'xls' };
    return { label: 'PDF', cls: '' };
  };

  // pre-filter by sidebar category; DataTable handles text search + status filter
  const filteredDocs = docs.filter((doc: any) => {
    const catOk = !catFilter.length || catFilter.some((id: any) => String(id) === String(doc.documentCategoryId));
    return catOk;
  });
  const pageDocs = filteredDocs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const signoff = (doc: any) => {
    const learn = doc.document_learn || [];
    const learnedCount = learn.filter((e: any) => e.status === 'learned').length;
    const newCount = learn.filter((e: any) => e.status === 'new').length;
    const total = learnedCount + newCount;
    const pct = total ? Math.round((learnedCount / total) * 100) : 0;
    return { learn, learnedCount, newCount, total, pct };
  };

  const roster = (learn: any[], status: string) => (
    <div className="text-xs" style={{ maxHeight: '300px', overflowY: 'auto' }}>
      {learn.slice()
        .sort((a: any, b: any) => a.user.first_name.localeCompare(b.user.first_name))
        .filter((e: any) => e.status === status)
        .map((e: any) => <div key={e.id}>{e.user.first_name} {e.user.last_name}</div>)}
    </div>
  );

  const catName = (id: any) => (category || []).find((c: any) => c.id === id)?.name || '—';

  const columns: ColDef[] = [
    {
      key: 'title',
      label: t('Title') as string,
      filterType: 'text',
      filterExtract: (doc: any) => `${doc.title || ''} ${doc.description || ''}`,
      render: (doc: any) => {
        const kind = fileKind(doc.file_url);
        return (
          <div className="dr-doc-main">
            <div className={`dr-doc-ico ${kind.cls}`}>{kind.label}</div>
            <div className="dr-doc-text">
              <div className="dr-doc-title">{doc.title}</div>
              {doc.description && <div className="dr-doc-desc">{doc.description}</div>}
            </div>
          </div>
        );
      },
    },
    {
      key: 'documentCategoryId',
      label: t('Categories') as string,
      width: 140,
      sortExtract: (doc: any) => catName(doc.documentCategoryId),
      render: (doc: any) => <span className="dr-cat-cell">{catName(doc.documentCategoryId)}</span>,
    },
    ...(access.description === -1 ? [{
      key: 'signoff',
      label: t('SignedOff') as string,
      width: 190,
      sortable: false,
      render: (doc: any) => {
        const { learn, learnedCount, newCount, total, pct } = signoff(doc);
        if (!total) return <span style={{ opacity: .4, fontSize: 13 }}>—</span>;
        return (
          <div className="dr-sig">
            <Tooltip placement="top" arrow title={roster(learn, 'learned')}>
              <span className="sbadge" style={{ background: 'rgba(5,150,105,.12)', color: '#059669' }}>✓ {learnedCount}</span>
            </Tooltip>
            <Tooltip placement="top" arrow title={roster(learn, 'new')}>
              <span className="sbadge" style={{ background: 'rgba(244,169,66,.14)', color: '#d97706' }}>⏳ {newCount}</span>
            </Tooltip>
            <span className="dr-sig-bar"><span style={{ width: `${pct}%` }} /></span>
          </div>
        );
      },
    } as ColDef] : []),
    {
      key: 'status',
      label: t('Status') as string,
      width: 165,
      sortable: false,
      filterType: 'select',
      filterOptions: [
        { value: 'learned', label: t('PolicyStatusAcknowledged') as string },
        { value: 'new', label: t('PolicyStatusPending') as string },
        { value: 'none_assigned', label: t('PolicyStatusNotAssigned') as string },
      ],
      filterExtract: (doc: any) => doc.needToLearnByUser?.status ?? 'none_assigned',
      render: (doc: any) => {
        const assigned = !!doc.needToLearnByUser;
        const learned = doc.needToLearnByUser?.status === 'learned';
        return (
          <span className={`sbadge ${!assigned ? '' : learned ? 'fulfilled' : 'pending'}`}
            style={!assigned ? { background: 'var(--bg-top)', color: 'var(--text-color-calendar-week)' } : {}}>
            {!assigned ? t('PolicyStatusNotAssigned') : learned ? `✓ ${t('PolicyStatusAcknowledged')}` : t('PolicyStatusPending')}
          </span>
        );
      },
    },
    ...(access.description === -1 ? [{
      key: 'actions',
      label: '',
      width: 60,
      sortable: false,
      render: (doc: any) => (
        <div className="dr-row-tools" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <span onClick={() => handleShow(doc.id, setShowEdit)}>
            <FontAwesomeIcon icon={faPenToSquare} />
          </span>
          <span onClick={() => handleShow(doc.id, setShowDelete)}>
            <FontAwesomeIcon icon={faTrashCan} />
          </span>
        </div>
      ),
    } as ColDef] : []),
  ];

  return (
    <>
      {viewMode === 'list' ? (
        <DataTable
          rows={filteredDocs}
          columns={columns}
          getKey={(r: any) => r.id}
          onRowClick={(doc: any) => setPanelDoc ? setPanelDoc(doc) : handleOpenPolicy(doc)}
          onRowDoubleClick={(doc: any) => handleOpenPolicy(doc)}
          selectedKey={panelDoc?.id}
          columnsToggle={false}
          exportable={false}
          pageSizes={[10, 20, 50]}
          defaultPageSize={20}
          noCard
          emptyText={t('No docs available') as string}
        />
      ) : (
        <>
          {filteredDocs.length ? (
            <>
              <div className="dr-gallery">
                {pageDocs.map((doc: any, index: any) => {
                  const assigned = !!doc.needToLearnByUser;
                  const learned = doc.needToLearnByUser?.status === 'learned';
                  const kind = fileKind(doc.file_url);
                  const { total, pct } = signoff(doc);
                  const statusLabel = !assigned
                    ? t('PolicyStatusNotAssigned')
                    : learned ? `✓ ${t('PolicyStatusAcknowledged')}` : t('PolicyStatusPending');

                  return (
                    <div key={doc.dateId || index} className="dr-gcard" onClick={() => handleOpenPolicy(doc)}>
                      <div className="dr-gcover">
                        <div className={`dr-doc-ico ${kind.cls} dr-gfmt`}>{kind.label}</div>
                        {doc.thumbnail_url ? (
                          <img
                            src={`${baseUrl.replace(/\/$/, '')}/${doc.thumbnail_url}`}
                            alt={doc.title}
                            className="dr-gthumb"
                          />
                        ) : (
                          <div className="dr-page"><i className="h" /><i /><i /><i /><i /><i /></div>
                        )}
                      </div>
                      <div className="dr-gbody">
                        <div className="dr-gt">{doc.title}</div>
                        <div className="dr-gcat">{catName(doc.documentCategoryId)}</div>
                        <div className="dr-gd">{doc.description}</div>
                        <div className="dr-gfoot">
                          <span className={`sbadge ${!assigned ? '' : learned ? 'fulfilled' : 'pending'}`}
                            style={!assigned ? { background: 'var(--bg-top)', color: 'var(--text-color-calendar-week)' } : {}}>
                            {statusLabel}
                          </span>
                          {access.description === -1 && total > 0 && (
                            <div className="dr-ring" style={{ background: `conic-gradient(#059669 ${pct}%, var(--border-input) 0)` }}>
                              <span>{pct}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="dr-list-foot">
                <Paginations
                  rows={filteredDocs}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  setRowsPerPage={setRowsPerPage}
                  setPage={setPage}
                  arrayPages={[10, 20, 50]}
                />
                <span className="flex gap-[5px] items-center">
                  {filteredDocs.length}
                  <FontAwesomeIcon icon={faFile} className="text-[13px] opacity-50" />
                </span>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center">
              <EmptyState message={t('No docs available') as string} />
            </div>
          )}
        </>
      )}

      {showReview && (
        <GoToReview showReview={showReview} setShowReview={setShowReview} sendToReview={sendToReview} />
      )}
      {showDelete && (
        <DeleteDocs showDelete={showDelete} setShowDelete={setShowDelete} refresh={refreshCategoryDocs} documentId={documentId} />
      )}

      <Modal open={showEdit} onClose={handleCloseEdit} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: '18px' }}>
        <>
          {showEdit && (
            <EditDocs
              category={category}
              selectedCategoryId={selectedCategoryId}
              handleClickCategoryBox={handleClickCategoryBox}
              categoryName={categoryName}
              isCategoryListVisible={isCategoryListVisible}
              handleCategoryListClick={handleCategoryListClick}
              handleNodeSelect={handleNodeSelect}
              handleClickAddCategoryId={handleClickAddCategoryId}
              refreshCategoryDocs={refreshCategoryDocs}
              documentId={documentId}
              setSelectedCategoryId={setSelectedCategoryId}
              setCategoryName={setCategoryName}
              none={none}
              handleCloseEdit={handleCloseEdit}
              baseUrl={baseUrl}
            />
          )}
        </>
      </Modal>
    </>
  );
};

export default DocsTable;
